import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import AdminDashboardModal from "../adminDashboard/adminDashboardModal";
import ReactApexChart from "react-apexcharts";
import { Calendar } from "primereact/calendar";
import { Nullable } from "primereact/ts-helpers";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import dayjs from "dayjs";
import { RootState } from "../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { fetchTeacherDashboardApi } from "../../../core/data/services/teacherDashboardService";
import {
  setLoading,
  setError,
  setTeacherDashboardData,
} from "../../../core/data/redux/teacherDashboardSlice";
import { getSchoolById } from "../../../core/data/services/schoolService";
import { setSelectedSchool } from "../../../core/data/redux/schoolSlice";
import { getUserDetails } from "../../../lib/getUserDetails";

// Define a separate interface for meals data to isolate it
interface MealsData {
  studentscount: Array<{
    className: string;
    sectionName: string;
    total: number;
  }>;
}

const HeadmasterDashboard = () => {
  const routes = all_routes;
  const dispatch = useDispatch();

  // New Global Filter States (replacing old global filter)
  const [periodType, setPeriodType] = useState<'day' | 'week' | 'month' | 'year' | 'custom'>('day');
  const [globalStartDate, setGlobalStartDate] = useState<Date>(dayjs().startOf("day").toDate());
  const [globalEndDate, setGlobalEndDate] = useState<Date>(dayjs().endOf("day").toDate());
  const [isGlobalFilterActive, setIsGlobalFilterActive] = useState(true);

  // Keep individual filter states for components that need them
  const [calendarDate, setCalendarDate] = useState<Nullable<Date>>(null);
  const [attendanceDateRange, setAttendanceDateRange] = useState<"today" | "thisWeek" | "lastWeek" | "lastMonth" | "custom">("today");
  const [mealsDateRange, setMealsDateRange] = useState<"today" | "thisWeek" | "lastWeek" | "lastMonth" | "custom">("today");
  const [attendanceDropdownOpen, setAttendanceDropdownOpen] = useState(false);
  const [mealsDropdownOpen, setMealsDropdownOpen] = useState(false);
  const [customAttendanceStartDate, setCustomAttendanceStartDate] = useState<Nullable<Date>>(null);
  const [customAttendanceEndDate, setCustomAttendanceEndDate] = useState<Nullable<Date>>(null);
  const [customMealsStartDate, setCustomMealsStartDate] = useState<Nullable<Date>>(null);
  const [customMealsEndDate, setCustomMealsEndDate] = useState<Nullable<Date>>(null);

  // Separate state for meals data
  const [mealsData, setMealsData] = useState<MealsData>({ studentscount: [] });

  // Teacher dashboard fetch data (for Attendance and other general data)
  const report = useSelector((state: RootState) => state.teacherDashboard.data);

  // User fetchData
  const user = useSelector((state: RootState) => state.user);

  // Define schoolId and defaultSchoolId before useCallback hooks
  // const users = user.Users;
  // const currentUser = users.length > 0 ? users[0] : null;
  // const schoolId = currentUser?.relatedEntity?.schools?.id;
  // console.log("headmaster dashboard ", schoolId);

  const userDetail = getUserDetails();
  const defaultSchoolId = userDetail[0]?.relatedEntity?.schools?.id;

    console.log("headmaster dashboard defaultSchoolID ", defaultSchoolId);


  // Fetch data for attendance and general dashboard data
  const fetchAttendanceData = useCallback(async (startDate: string, endDate: string) => {
    dispatch(setLoading(true));
    try {
      const response = await fetchTeacherDashboardApi({
        startDate,
        endDate,
        schoolIds: defaultSchoolId,
        isCumulative: true,
      });
      dispatch(setTeacherDashboardData(response));
      console.log("Attendance/General data", response);
    } catch (error: any) {
      dispatch(setError(error.message));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, defaultSchoolId]);

  // Fetch data specifically for meals
  const fetchMealsData = useCallback(async (startDate: string, endDate: string) => {
    try {
      const response = await fetchTeacherDashboardApi({
        startDate,
        endDate,
        schoolIds: defaultSchoolId,
        isCumulative: false,
      });
      setMealsData({ studentscount: response.studentscount || [] });
      console.log("Meals data", response);
    } catch (error: any) {
      console.error("Error fetching meals data:", error.message);
    }
  }, [defaultSchoolId]);

  // Global Filter Handlers
  useEffect(() => {
    if (periodType === 'custom') return; // Don't auto-update for custom

    let newStart: any, newEnd: any;
    if (periodType === 'day') {
      newStart = dayjs().toDate();
      newEnd = dayjs().toDate();
    } else if (periodType === 'week') {
      newStart = dayjs().startOf('week').toDate();
      newEnd = dayjs().endOf('week').toDate();
    } else if (periodType === 'month') {
      newStart = dayjs().startOf('month').toDate();
      newEnd = dayjs().endOf('month').toDate();
    } else if (periodType === 'year') {
      newStart = dayjs().startOf('year').toDate();
      newEnd = dayjs().endOf('year').toDate();
    }

    setGlobalStartDate(newStart);
    setGlobalEndDate(newEnd);
    setIsGlobalFilterActive(true);

    // Fetch data with new dates
    const startStr = dayjs(newStart).format('YYYY-MM-DD');
    const endStr = dayjs(newEnd).format('YYYY-MM-DD');
    fetchAttendanceData(startStr, endStr);
    fetchMealsData(startStr, endStr);
  }, [periodType, fetchAttendanceData, fetchMealsData]);

  const handlePrev = () => {
    let newStart, newEnd;
    if (periodType === 'day') {
      newStart = dayjs(globalStartDate).subtract(1, 'day').toDate();
      newEnd = dayjs(globalEndDate).subtract(1, 'day').toDate();
    } else if (periodType === 'week') {
      newStart = dayjs(globalStartDate).subtract(1, 'week').toDate();
      newEnd = dayjs(globalEndDate).subtract(1, 'week').toDate();
    } else if (periodType === 'month') {
      newStart = dayjs(globalStartDate).subtract(1, 'month').toDate();
      newEnd = dayjs(globalEndDate).subtract(1, 'month').toDate();
    } else if (periodType === 'year') {
      newStart = dayjs(globalStartDate).subtract(1, 'year').toDate();
      newEnd = dayjs(globalEndDate).subtract(1, 'year').toDate();
    } else {
      // Custom mode - subtract based on current range
      const diff = dayjs(globalEndDate).diff(dayjs(globalStartDate), 'days');
      newStart = dayjs(globalStartDate).subtract(diff + 1, 'days').toDate();
      newEnd = dayjs(globalEndDate).subtract(diff + 1, 'days').toDate();
    }

    setGlobalStartDate(newStart);
    setGlobalEndDate(newEnd);

    const startStr = dayjs(newStart).format('YYYY-MM-DD');
    const endStr = dayjs(newEnd).format('YYYY-MM-DD');
    fetchAttendanceData(startStr, endStr);
    fetchMealsData(startStr, endStr);
  };

  const handleNext = () => {
    let newStart, newEnd;
    if (periodType === 'day') {
      newStart = dayjs(globalStartDate).add(1, 'day').toDate();
      newEnd = dayjs(globalEndDate).add(1, 'day').toDate();
    } else if (periodType === 'week') {
      newStart = dayjs(globalStartDate).add(1, 'week').toDate();
      newEnd = dayjs(globalEndDate).add(1, 'week').toDate();
    } else if (periodType === 'month') {
      newStart = dayjs(globalStartDate).add(1, 'month').toDate();
      newEnd = dayjs(globalEndDate).add(1, 'month').toDate();
    } else if (periodType === 'year') {
      newStart = dayjs(globalStartDate).add(1, 'year').toDate();
      newEnd = dayjs(globalEndDate).add(1, 'year').toDate();
    } else {
      // Custom mode - add based on current range
      const diff = dayjs(globalEndDate).diff(dayjs(globalStartDate), 'days');
      newStart = dayjs(globalStartDate).add(diff + 1, 'days').toDate();
      newEnd = dayjs(globalEndDate).add(diff + 1, 'days').toDate();
    }

    setGlobalStartDate(newStart);
    setGlobalEndDate(newEnd);

    const startStr = dayjs(newStart).format('YYYY-MM-DD');
    const endStr = dayjs(newEnd).format('YYYY-MM-DD');
    fetchAttendanceData(startStr, endStr);
    fetchMealsData(startStr, endStr);
  };

  const handleGlobalDateChange = (which: 'start' | 'end', value: string) => {
    if (which === 'start') {
      const newDate = value ? new Date(value) : globalStartDate;
      setGlobalStartDate(newDate);
    } else {
      const newDate = value ? new Date(value) : globalEndDate;
      setGlobalEndDate(newDate);
    }
    setPeriodType('custom');

    // Fetch data after a short delay to allow both dates to be set
    setTimeout(() => {
      const startStr = dayjs(which === 'start' ? (value ? new Date(value) : globalStartDate) : globalStartDate).format('YYYY-MM-DD');
      const endStr = dayjs(which === 'end' ? (value ? new Date(value) : globalEndDate) : globalEndDate).format('YYYY-MM-DD');
      fetchAttendanceData(startStr, endStr);
      fetchMealsData(startStr, endStr);
    }, 100);
  };

  // Load school data when component mounts
  useEffect(() => {
    const loadSchoolData = async () => {
      try {
        // const schools = await fetchAllSchools();
        // dispatch(setSchoolList(schools));

        const school = await getSchoolById(defaultSchoolId);

        const userSchool = school;
        if (userSchool) {
          dispatch(setSelectedSchool(userSchool));
        } else {
          console.error('School not found for user:', user?.Userdetail?.schoolName);
        }
      } catch (error) {
        console.error('Error loading school data:', error);
      }
    };

    if (user?.Userdetail?.schoolName) {
      loadSchoolData();
    }
  }, [dispatch, user?.Userdetail?.schoolName, defaultSchoolId]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [classDonutChart, setClassDonutChart] = useState<any>({
    chart: {
      height: 218,
      width: 218,
      type: "donut",
      toolbar: { show: false },
    },
    labels: ["Total", "Male", "Female"],
    legend: { show: false },
    dataLabels: { enabled: false },
    yaxis: { tickAmount: 3, labels: { offsetX: -15 } },
    grid: { padding: { left: -8 } },
    colors: ["#3D5EE1", "#EAB300", "#E82646"],
    series: [0, 0, 0],
    responsive: [{ breakpoint: 480, options: { chart: { width: 180 } } }],
  });

  useEffect(() => {
    if (report?.studentscount?.[selectedIndex]) {
      const selectedClass = report.studentscount[selectedIndex];
      setClassDonutChart((prevChart: any) => ({
        ...prevChart,
        series: [
          selectedClass?.total ?? 0,
          selectedClass?.male ?? 0,
          selectedClass?.female ?? 0,
        ],
      }));
    }
  }, [report, selectedIndex]);

  const [mealsChart, setMealsChart] = useState<any>({
    chart: {
      height: 275,
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
    },
    legend: {
      show: true,
      horizontalAlign: 'left',
      position: 'top',
      fontSize: '14px',
      labels: { colors: '#5D6369' },
    },
    plotOptions: { bar: { horizontal: false, columnWidth: '50%', endingShape: 'rounded' } },
    colors: ['#3D5EE1', '#E9EDF4'],
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ['transparent'] },
    grid: { padding: { left: -8 } },
    series: [{ name: 'Meals Served', data: [] }, { name: 'Total Students', data: [] }],
    xaxis: { categories: [] },
    yaxis: { tickAmount: 3, labels: { offsetX: -15 } },
    fill: { opacity: 1 },
    tooltip: { y: { formatter: (val: any) => `${val} students` } },
  });

  useEffect(() => {
    if (mealsData?.studentscount && Array.isArray(mealsData.studentscount)) {
      const categories = mealsData.studentscount.map(cls => `${cls.className}-${cls.sectionName}`);
      const totalData = mealsData.studentscount.map(cls => cls.total);
      const mealsServedData = mealsData.studentscount.map(cls => Math.floor(cls.total * 0.8));

      setMealsChart((prevChart: any) => ({
        ...prevChart,
        series: [
          { name: 'Meals Served', data: mealsServedData },
          { name: 'Total Students', data: totalData },
        ],
        xaxis: { categories },
      }));
    }
  }, [mealsData]);

  const [attendanceChart, setAttendanceChart] = useState<any>({
    chart: { height: 290, type: "donut", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: "50%" } },
    dataLabels: { enabled: false },
    series: [0, 0, 0],
    labels: ["Present", "Absent", "OD"],
    colors: ["#1ABE17", "#E82646", "#1170E4"],
    responsive: [
      { breakpoint: 480, options: { chart: { width: 200 }, legend: { position: "left" } } },
    ],
    legend: { position: "bottom" },
  });

  useEffect(() => {
    if (report?.cumulative) {
      setAttendanceChart((prevChart: any) => ({
        ...prevChart,
        series: [
          report.cumulative.studentsPresent ?? 0,
          report.cumulative.studentsAbsent ?? 0,
          report.cumulative.studentOdCount ?? 0,
        ],
      }));
    }
  }, [report]);

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
  };

  const selectedClass = report?.studentscount?.[selectedIndex];
  const totalTeachers = report?.studentscount?.length || 0;

  const getDatesByRange = (range: string, customStart?: Date | null, customEnd?: Date | null) => {
    const today = dayjs();
    if (range === "custom" && customStart && customEnd) {
      return {
        startDate: dayjs(customStart).format("YYYY-MM-DD"),
        endDate: dayjs(customEnd).format("YYYY-MM-DD"),
      };
    }

    switch (range) {
      case "today":
        return { startDate: today.format("YYYY-MM-DD"), endDate: today.format("YYYY-MM-DD") };
      case "thisWeek":
        return {
          startDate: today.startOf("week").format("YYYY-MM-DD"),
          endDate: today.endOf("week").format("YYYY-MM-DD"),
        };
      case "lastWeek":
        return {
          startDate: today.subtract(1, "week").startOf("week").format("YYYY-MM-DD"),
          endDate: today.subtract(1, "week").endOf("week").format("YYYY-MM-DD"),
        };
      case "lastMonth":
        return {
          startDate: today.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
          endDate: today.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
        };
      default:
        return { startDate: today.format("YYYY-MM-DD"), endDate: today.format("YYYY-MM-DD") };
    }
  };

  // Individual filter handlers (for components not affected by global filter)
  useEffect(() => {
    if (isGlobalFilterActive || calendarDate) return;
    const { startDate, endDate } = getDatesByRange(attendanceDateRange, customAttendanceStartDate, customAttendanceEndDate);
    fetchAttendanceData(startDate, endDate);
  }, [attendanceDateRange, customAttendanceStartDate, customAttendanceEndDate, isGlobalFilterActive, calendarDate, fetchAttendanceData]);

  useEffect(() => {
    if (isGlobalFilterActive || calendarDate) return;
    const { startDate, endDate } = getDatesByRange(mealsDateRange, customMealsStartDate, customMealsEndDate);
    fetchMealsData(startDate, endDate);
  }, [mealsDateRange, customMealsStartDate, customMealsEndDate, isGlobalFilterActive, calendarDate, fetchMealsData]);

  // initial load
  useEffect(() => {
  const today = dayjs().format("YYYY-MM-DD");
  fetchAttendanceData(today, today);
  fetchMealsData(today, today);
}, [fetchAttendanceData, fetchMealsData]);

  useEffect(() => {
    if (!calendarDate) return;
    const selectedDate = dayjs(calendarDate).format("YYYY-MM-DD");
    setIsGlobalFilterActive(false);
    fetchAttendanceData(selectedDate, selectedDate);
    fetchMealsData(selectedDate, selectedDate);
  }, [calendarDate, fetchAttendanceData, fetchMealsData]);

  const handleAttendanceRangeChange = (range: string) => {
    setAttendanceDateRange(range as any);
    setCalendarDate(null);
    setIsGlobalFilterActive(false);
    setAttendanceDropdownOpen(false);

    if (range !== "custom") {
      setCustomAttendanceStartDate(null);
      setCustomAttendanceEndDate(null);
    }
  };

  const handleMealsRangeChange = (range: string) => {
    setMealsDateRange(range as any);
    setCalendarDate(null);
    setIsGlobalFilterActive(false);
    setMealsDropdownOpen(false);

    if (range !== "custom") {
      setCustomMealsStartDate(null);
      setCustomMealsEndDate(null);
    }
  };

  const handleCalendarChange = (e: any) => {
    setCalendarDate(e.value);
    setIsGlobalFilterActive(false);
  };

  const getDisplayText = (range: string, customStart?: Date | null, customEnd?: Date | null) => {
    if (isGlobalFilterActive && globalStartDate && globalEndDate) {
      return `Global Filter: ${dayjs(globalStartDate).format("MMM DD")} - ${dayjs(globalEndDate).format("MMM DD, YYYY")}`;
    }
    if (calendarDate) {
      return dayjs(calendarDate).format("MMM DD, YYYY");
    }
    if (range === "custom" && customStart && customEnd) {
      return `${dayjs(customStart).format("MMM DD")} - ${dayjs(customEnd).format("MMM DD, YYYY")}`;
    }
    return {
      today: "Today",
      thisWeek: "This Week",
      lastWeek: "Last Week",
      lastMonth: "Last Month",
      custom: "Custom Range",
    }[range];
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="d-md-flex align-items-center justify-content-between mb-4">
            <div>
              <h3 className="page-title mb-1">Headmaster Dashboard</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.HeadmasterDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Headmaster Dashboard</li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="card bg-dark">
            <div className="overlay-img">
              <ImageWithBasePath src="assets/img/bg/shape-04.png" alt="img" className="img-fluid shape-01" />
              <ImageWithBasePath src="assets/img/bg/shape-01.png" alt="img" className="img-fluid shape-02" />
              <ImageWithBasePath src="assets/img/bg/shape-02.png" alt="img" className="img-fluid shape-03" />
              <ImageWithBasePath src="assets/img/bg/shape-03.png" alt="img" className="img-fluid shape-04" />
            </div>
            <div className="card-body">
              <div className="d-flex align-items-xl-center justify-content-xl-between flex-xl-row flex-column">
                <div className="mb-3 mb-xl-0">
                  <div className="d-flex align-items-center flex-wrap mb-2">
                    <h1 className="text-white me-2">
                      Welcome Back, {user?.Userdetail?.firstName} {user?.Userdetail?.lastName}
                    </h1>
                    <Link to={routes.userViewProfile} className="avatar avatar-sm img-rounded bg-gray-800 dark-hover">
                      <i className="ti ti-edit text-white" />
                    </Link>
                  </div>
                  <p className="text-white">Have a Good day at work</p>
                </div>
              </div>
            </div>
          </div>

          {/* Global Date Filter - New UI */}
          <div className="card mb-4">
            <div className="card-body py-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div className="d-flex align-items-center gap-3">
                  {/* Period Type Tabs */}
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${periodType === 'day' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                      onClick={() => setPeriodType('day')}
                    >
                      Day
                    </button>
                    <button
                      type="button"
                      className={`btn ${periodType === 'week' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                      onClick={() => setPeriodType('week')}
                    >
                      Week
                    </button>
                    <button
                      type="button"
                      className={`btn ${periodType === 'month' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                      onClick={() => setPeriodType('month')}
                    >
                      Month
                    </button>
                    <button
                      type="button"
                      className={`btn ${periodType === 'year' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                      onClick={() => setPeriodType('year')}
                    >
                      Year
                    </button>
                  </div>

                  {/* Navigation Arrows */}
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handlePrev}
                      title="Previous"
                    >
                      <i className="ti ti-chevron-left"></i>
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleNext}
                      title="Next"
                    >
                      <i className="ti ti-chevron-right"></i>
                    </button>
                  </div>
                </div>

                {/* Date Range Inputs */}
                <div className="d-flex align-items-center gap-2">
                  <div className="position-relative">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ width: '150px', paddingRight: '35px' }}
                      value={globalStartDate ? dayjs(globalStartDate).format("YYYY-MM-DD") : ""}
                      max={globalEndDate ? dayjs(globalEndDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")}
                      onChange={(e) => handleGlobalDateChange("start", e.target.value)}
                    />
                    <i className="ti ti-calendar position-absolute" style={{
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: '#6c757d'
                    }}></i>
                  </div>
                  <span className="text-muted">to</span>
                  <div className="position-relative">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ width: '150px', paddingRight: '35px' }}
                      value={globalEndDate ? dayjs(globalEndDate).format("YYYY-MM-DD") : ""}
                      min={globalStartDate ? dayjs(globalStartDate).format("YYYY-MM-DD") : ""}
                      max={dayjs().format("YYYY-MM-DD")}
                      onChange={(e) => handleGlobalDateChange("end", e.target.value)}
                    />
                    <i className="ti ti-calendar position-absolute" style={{
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: '#6c757d'
                    }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-xxl-3 col-sm-6 d-flex">
              <div className="card flex-fill animate-card border-0">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-xl bg-danger-transparent me-2 p-1">
                      <ImageWithBasePath src="assets/img/icons/student.svg" alt="img" />
                    </div>
                    <div className="overflow-hidden flex-fill">
                      <div className="d-flex align-items-center justify-content-between">
                        <h2 className="counter">{report?.cumulative?.total || 0}</h2>
                      </div>
                      <p className="mb-1">Total Students</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-between border-top mt-3 pt-3">
                    <p className="mb-0">
                      Male: <span className="text-dark fw-semibold">{report?.cumulative?.male || 0}</span>
                    </p>
                    <span className="text-light">|</span>
                    <p>
                      Female: <span className="text-dark fw-semibold">{report?.cumulative?.female || 0}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xxl-3 col-sm-6 d-flex">
              <div className="card flex-fill animate-card border-0">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-xl me-2 bg-secondary-transparent p-1">
                      <ImageWithBasePath src="assets/img/icons/teacher.svg" alt="img" />
                    </div>
                    <div className="overflow-hidden flex-fill">
                      <div className="d-flex align-items-center justify-content-between">
                        <h2 className="counter">{totalTeachers}</h2>
                        <span className="badge bg-pending">Active</span>
                      </div>
                      <p>Total Classes</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-between border-top mt-3 pt-3">
                    <p className="mb-0">
                      Classes: <span className="text-dark fw-semibold">{totalTeachers}</span>
                    </p>
                    <span className="text-light">|</span>
                    <p>
                      Sections: <span className="text-dark fw-semibold">{totalTeachers}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xxl-3 col-sm-6 d-flex">
              <div className="card flex-fill animate-card border-0">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-xl me-2 bg-warning-transparent p-1">
                      <ImageWithBasePath src="assets/img/icons/staff.svg" alt="img" />
                    </div>
                    <div className="overflow-hidden flex-fill">
                      <div className="d-flex align-items-center justify-content-between">
                        <h2 className="counter">
                          {(report?.cumulative?.mealsServedCount || 0) + (report?.cumulative?.melasNotServedCount || 0)}
                        </h2>
                      </div>
                      <p>Total Meals</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-between border-top mt-3 pt-3">
                    <p className="mb-0">
                      Served: <span className="text-dark fw-semibold">{report?.cumulative?.mealsServedCount || 0}</span>
                    </p>
                    <span className="text-light">|</span>
                    <p>
                      Not Served: <span className="text-dark fw-semibold">{report?.cumulative?.melasNotServedCount || 0}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xxl-3 col-sm-6 d-flex">
              <div className="card flex-fill animate-card border-0">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-xl me-2 bg-success-transparent p-1">
                      <ImageWithBasePath src="assets/img/icons/subject.svg" alt="img" />
                    </div>
                    <div className="overflow-hidden flex-fill">
                      <div className="d-flex align-items-center justify-content-between">
                        <h2 className="counter">{report?.cumulative?.studentsPresent || 0}</h2>
                      </div>
                      <p>Students Present</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-between border-top mt-3 pt-3">
                    <p className="mb-0">
                      Present: <span className="text-dark fw-semibold">{report?.cumulative?.studentsPresent || 0}</span>
                    </p>
                    <span className="text-light">|</span>
                    <p>
                      Absent: <span className="text-dark fw-semibold">{report?.cumulative?.studentsAbsent || 0}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-xxl-7 col-xl-12 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h4 className="card-title">Class-wise Students Distribution</h4>
                  <div className="card-dropdown">
                    <button
                      className="btn dropdown-toggle p-0 border-0 bg-transparent"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded={mealsDropdownOpen}
                      onClick={() => setMealsDropdownOpen(!mealsDropdownOpen)}
                      disabled={isGlobalFilterActive}
                    >
                      <i className="ti ti-calendar-due" />
                      {getDisplayText(mealsDateRange, customMealsStartDate, customMealsEndDate)}
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      {["today", "thisWeek", "lastWeek", "lastMonth", "custom"].map(r => (
                        <li key={r}>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleMealsRangeChange(r)}
                          >
                            {r === "custom" ? "Custom Range" : r.charAt(0).toUpperCase() + r.slice(1)}
                          </button>
                        </li>
                      ))}
                    </ul>
                    {mealsDateRange === "custom" && (
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={customMealsStartDate ? dayjs(customMealsStartDate).format("YYYY-MM-DD") : ""}
                          onChange={(e) => setCustomMealsStartDate(e.target.value ? new Date(e.target.value) : null)}
                          max={new Date().toISOString().split('T')[0]}
                        />
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={customMealsEndDate ? dayjs(customMealsEndDate).format("YYYY-MM-DD") : ""}
                          onChange={(e) => setCustomMealsEndDate(e.target.value ? new Date(e.target.value) : null)}
                          max={new Date().toISOString().split('T')[0]}
                          min={customMealsStartDate ? dayjs(customMealsStartDate).format("YYYY-MM-DD") : ""}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="card-body pb-0">
                  <ReactApexChart
                    id="meals-chart"
                    options={mealsChart}
                    series={mealsChart.series}
                    type="bar"
                    height={270}
                  />
                </div>
              </div>
            </div>
            <div className="col-xxl-5 col-xl-12 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h4 className="card-title">Attendance Overview</h4>
                  <div className="card-dropdown">
                    <button
                      className="btn dropdown-toggle p-0 border-0 bg-transparent"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded={attendanceDropdownOpen}
                      onClick={() => setAttendanceDropdownOpen(!attendanceDropdownOpen)}
                      disabled={isGlobalFilterActive}
                    >
                      <i className="ti ti-calendar-due" />
                      {getDisplayText(attendanceDateRange, customAttendanceStartDate, customAttendanceEndDate)}
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      {["today", "thisWeek", "lastWeek", "lastMonth", "custom"].map(r => (
                        <li key={r}>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleAttendanceRangeChange(r)}
                          >
                            {r === "custom" ? "Custom Range" : r.charAt(0).toUpperCase() + r.slice(1)}
                          </button>
                        </li>
                      ))}
                    </ul>
                    {attendanceDateRange === "custom" && (
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={customAttendanceStartDate ? dayjs(customAttendanceStartDate).format("YYYY-MM-DD") : ""}
                          onChange={(e) => setCustomAttendanceStartDate(e.target.value ? new Date(e.target.value) : null)}
                          max={new Date().toISOString().split('T')[0]}
                        />
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={customAttendanceEndDate ? dayjs(customAttendanceEndDate).format("YYYY-MM-DD") : ""}
                          onChange={(e) => setCustomAttendanceEndDate(e.target.value ? new Date(e.target.value) : null)}
                          max={new Date().toISOString().split('T')[0]}
                          min={customAttendanceStartDate ? dayjs(customAttendanceStartDate).format("YYYY-MM-DD") : ""}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="card-body pb-0">
                  <p className="mb-3">
                    <i className="ti ti-calendar-heart text-primary me-2" />
                    Total Students <span className="fw-medium text-dark">{report?.cumulative?.total || 0}</span>
                  </p>
                  <div className="border rounded p-3">
                    <div className="row">
                      <div className="col text-center border-end">
                        <p className="mb-1">Present</p>
                        <h5 className="text-success">{report?.cumulative?.studentsPresent || 0}</h5>
                      </div>
                      <div className="col text-center border-end">
                        <p className="mb-1">Absent</p>
                        <h5 className="text-danger">{report?.cumulative?.studentsAbsent || 0}</h5>
                      </div>
                      <div className="col text-center">
                        <p className="mb-1">OD</p>
                        <h5 className="text-primary">{report?.cumulative?.studentOdCount || 0}</h5>
                      </div>
                    </div>
                  </div>
                  <div className="attendance-chart text-center">
                    <ReactApexChart
                      id="attendance_chart"
                      className="mb-3 mb-sm-0 text-center text-sm-start"
                      options={attendanceChart}
                      series={attendanceChart.series}
                      type="donut"
                      height={250}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-xxl-8 col-xl-12">
              <div className="card flex-fill">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h4 className="card-title">Class Details</h4>
                  <div className="dropdown">
                    <Link
                      to="#"
                      className="bg-white dropdown-toggle"
                      data-bs-toggle="dropdown"
                    >
                      <i className="ti ti-school-bell me-2" />
                      {selectedClass?.className} - {selectedClass?.sectionName}
                    </Link>
                    <ul className="dropdown-menu mt-2 p-3">
                      {report?.studentscount?.map((cls, index) => (
                        index !== selectedIndex && (
                          <li key={index}>
                            <Link
                              to="#"
                              className="dropdown-item rounded-1"
                              onClick={() => handleSelect(index)}
                            >
                              {cls.className} - {cls.sectionName}
                            </Link>
                          </li>
                        )
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="card-body">
                  <div className="d-md-flex align-items-center justify-content-between">
                    <div className="me-md-3 mb-3 mb-md-0 w-100">
                      <div className="border border-dashed p-3 rounded d-flex align-items-center justify-content-between mb-1">
                        <p className="mb-0 me-2">
                          <i className="ti ti-arrow-badge-down-filled me-2 text-primary" />
                          Total Students
                        </p>
                        <h5>{selectedClass?.total || 0}</h5>
                      </div>
                      <div className="border border-dashed p-3 rounded d-flex align-items-center justify-content-between mb-1">
                        <p className="mb-0 me-2">
                          <i className="ti ti-arrow-badge-down-filled me-2 text-warning" />
                          Male Students
                        </p>
                        <h5>{selectedClass?.male || 0}</h5>
                      </div>
                      <div className="border border-dashed p-3 rounded d-flex align-items-center justify-content-between mb-0">
                        <p className="mb-0 me-2">
                          <i className="ti ti-arrow-badge-down-filled me-2 text-danger" />
                          Female Students
                        </p>
                        <h5>{selectedClass?.female || 0}</h5>
                      </div>
                    </div>
                    <ReactApexChart
                      id="class-chart"
                      className="text-center text-md-left"
                      options={classDonutChart}
                      series={classDonutChart.series}
                      type="donut"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex justify-content-between">
                  <h4 className="card-title">Schedules</h4>
                  {calendarDate && (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setCalendarDate(null)}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="card-body">
                  <Calendar
                    inline
                    value={calendarDate}
                    onChange={handleCalendarChange}
                    className="custom-cal-react"
                    maxDate={new Date()}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AdminDashboardModal />
    </>
  );
};

export default HeadmasterDashboard;
